<template>
    <section class="flex flex-col gap-4" data-test="rnd-run-approval">
        <div class="flex flex-col gap-2 text-sm text-slate-800">
            <p class="m-0" data-test="approval-summary">
                This run found {{ contactsLabel(counts?.reassigned) }} with reassigned numbers:
                {{ countLabel(reassigned?.not_yet_applied) }} to reset, {{ countLabel(reassigned?.active_since) }} left
                alone because they have opted in or out since the number changed hands, and
                {{ countLabel(reassigned?.changed) }} left alone because they changed during the run. Nobody has been
                reset yet.
            </p>
            <p class="m-0" data-test="approval-effect">
                Approving resets every contact marked Reset: its name, email, tags, custom attributes and location
                memberships are cleared, it leaves campaigns and workflows, and both its subscriptions are set to no
                preference. Each contact is read again first: one that has opted in or out since the query is left
                alone, and so is one whose subscriptions or opt-in date attribute have changed in any other way, for
                Check remaining contacts to check again. Denying resets nobody and keeps the results.
            </p>
            <p class="m-0 font-bold" data-test="approval-expiry">
                If nobody approves or denies it by {{ runDateLabel(props.run.approval_expires_at) }}, it expires with
                nothing reset.
            </p>
        </div>

        <vx-data-table
            :columns="kioskRndApprovalColumns"
            :rows="contacts"
            rowKey="contact_id"
            :loading="loading"
            :hasMore="hasMore"
            :loadingMore="loadingMore"
            @loadMore="loadMore"
        >
            <template v-slot:cell-phone="{ row }">
                <span class="tabular-nums">{{ formatPhoneNumber(row.phone) }}</span>
            </template>

            <template v-slot:cell-name="{ row }">{{ contactNameLabel(row.name) }}</template>

            <template v-slot:cell-consent_date="{ row }">
                {{ calendarDateLabel(row.consent_date) }}
                <span class="text-slate-500">· {{ consentSourceLabels[row.consent_source] }}</span>
            </template>

            <template v-slot:cell-decision="{ row }">
                <div v-if="row.decision !== null" class="flex min-w-0 items-center gap-2">
                    <vx-badge size="xs" :color="decisionColors[row.decision]" data-test="decision">
                        {{ decisionLabels[row.decision] }}
                    </vx-badge>
                    <span v-if="row.decision === 'active'" class="min-w-0 truncate" data-test="active-since">
                        {{ activeSinceLabel(row) }}
                    </span>
                </div>
                <span v-else class="text-slate-500">-</span>
            </template>

            <template v-slot:empty>
                <div class="flex flex-col items-center gap-6 text-xl text-slate-500">No reassigned contacts</div>
            </template>
        </vx-data-table>
    </section>
</template>

<script lang="ts" setup>
import { VxBadge, VxDataTable } from '@voxie/frontend-components';
import { isCancel } from 'axios';
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { formatPhoneNumber } from '~/components/filters';
import {
    calendarDateLabel,
    consentSourceLabels,
    contactsLabel,
    countLabel,
    runDateLabel,
} from '~/components/kiosk/kiosk-reassigned-numbers/rndLabels';
import {
    RND_RUN_CONTACTS_PER_PAGE,
    activeSinceLabel,
    contactNameLabel,
    decisionColors,
    decisionLabels,
    kioskRndApprovalColumns,
} from '~/components/kiosk/kiosk-reassigned-numbers/runs/kioskRndApprovalHelpers';
import { nextCursorFrom } from '~/components/kiosk/kiosk-reassigned-numbers/runs/kioskRndHistoryHelpers';
import { useToasted } from '~/composables/useToasted';
import { adminRndClient } from '~/services';
import type { RndRun, RndRunContact } from '~/services/adminRndClient.types';

const props = defineProps<{ run: RndRun }>();

const toasted = useToasted();

const contactsCancelKey = Symbol('kiosk-rnd-run-contacts');

const contacts = ref<RndRunContact[]>([]);
const nextCursor = ref<string | null>(null);
const loading = ref(true);
const loadingMore = ref(false);

const counts = computed(() => props.run.counts);
const reassigned = computed(() => props.run.counts?.reassigned_breakdown);
const hasMore = computed(() => nextCursor.value !== null);

const loadContacts = async () => {
    loading.value = true;

    try {
        const { data } = await adminRndClient.getRunContacts(
            props.run.id,
            { per_page: RND_RUN_CONTACTS_PER_PAGE },
            { cancelKey: contactsCancelKey }
        );

        contacts.value = data.data;
        nextCursor.value = nextCursorFrom(data.next_page_url);
        loading.value = false;
    } catch (error) {
        if (isCancel(error)) {
            return;
        }

        contacts.value = [];
        loading.value = false;
        toasted.global.platform_error();
    }
};

const loadMore = async () => {
    loadingMore.value = true;

    try {
        const { data } = await adminRndClient.getRunContacts(
            props.run.id,
            { per_page: RND_RUN_CONTACTS_PER_PAGE, cursor: nextCursor.value },
            { cancelKey: contactsCancelKey }
        );

        contacts.value = [...contacts.value, ...data.data];
        nextCursor.value = nextCursorFrom(data.next_page_url);
        loadingMore.value = false;
    } catch (error) {
        if (isCancel(error)) {
            return;
        }

        loadingMore.value = false;
        toasted.global.platform_error();
    }
};

onMounted(() => {
    loadContacts();
});

onBeforeUnmount(() => {
    adminRndClient.cancelRequest(contactsCancelKey);
});
</script>
